import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { trackRumAction } from "@/lib/datadog";
import { Button } from "@/components/ui/button";
import { Check, ChevronDown, Info } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const VARIABLE_TYPE_LABELS: Record<string, string> = {
  model: "Model",
  weather: "Weather",
  threat: "Threat",
};

const VARIABLE_TYPE_ORDER = ["model", "weather", "threat"] as const;

// Define types for the properties that can be displayed
export interface PropertyOption {
  id: string;
  label: string;
  description?: string | null;
  type?: string | null;
  summary?: string | null;
  units?: string | null;
  order?: number;
}

export interface MapPropertySelectorProps {
  properties: PropertyOption[];
  selectedProperty?: PropertyOption;
  onPropertyChange?: (propertyId: string) => void;
  className?: string;
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  defaultPropertyId?: string;
  loading?: boolean;
}

/**
 * MapPropertySelector Component
 *
 * A compact property selector for choosing which property to display on the map.
 * Supports ref forwarding so parent components can observe its bounding rect
 * (e.g. for legend overlap detection).
 */
const MapPropertySelector = React.forwardRef<
  HTMLDivElement,
  MapPropertySelectorProps
>(
  (
    {
      properties,
      selectedProperty: controlledSelected,
      onPropertyChange,
      className = "",
      position = "top-right",
      defaultPropertyId,
      loading = false,
    },
    ref,
  ) => {
    const currentSelectorState = {
      properties,
      selectedProperty: controlledSelected,
      defaultPropertyId,
    };
    const committedSelectorStateRef = useRef(currentSelectorState);

    if (!loading) {
      committedSelectorStateRef.current = currentSelectorState;
    }

    const shouldPreserveSelector =
      loading && committedSelectorStateRef.current.properties.length > 0;
    const selectorState = shouldPreserveSelector
      ? committedSelectorStateRef.current
      : currentSelectorState;
    const displayProperties = selectorState.properties;
    const displayControlledSelected = selectorState.selectedProperty;
    const displayDefaultPropertyId = selectorState.defaultPropertyId;

    // Position styles
    const positionStyles = {
      "top-left": "absolute top-2 left-2 z-[9999] sm:top-4 sm:left-4",
      "top-right": "absolute top-2 right-2 z-[10] sm:right-4",
      "bottom-left": "absolute bottom-3 left-2 z-[9999] sm:bottom-4 sm:left-4",
      "bottom-right": "absolute bottom-3 right-2 z-[9999] sm:bottom-4 sm:right-4",
    };
    const triggerClassName =
      "h-8 w-[min(12rem,calc(100vw-1rem))] justify-between bg-background/95 text-xs shadow-md backdrop-blur-sm sm:w-48 sm:text-sm lg:w-full";
    const containerClassName = `w-auto lg:w-56 lg:gap-0 lg:rounded-xl lg:border lg:bg-background/95 lg:py-4 lg:shadow-lg lg:backdrop-blur-sm ${positionStyles[position]} ${className}`;

    // Find default property (recomputed when defaultPropertyId or properties change)
    const defaultProperty = displayDefaultPropertyId
      ? displayProperties.find((p) => p.id === displayDefaultPropertyId)
      : displayProperties[0];
    const effectiveDefault = defaultProperty || displayProperties[0];

    // State for selected property (uncontrolled mode)
    const [internalSelected, setInternalSelected] =
      useState<PropertyOption | null>(effectiveDefault ?? null);

    // Sync internal state when defaultPropertyId or properties change (e.g. model change)
    const propertyIdsKey = displayProperties.map((p) => p.id).join(",");
    useEffect(() => {
      if (!displayControlledSelected && effectiveDefault) {
        setInternalSelected(effectiveDefault);
      }
    }, [
      displayDefaultPropertyId,
      propertyIdsKey,
      displayControlledSelected,
      effectiveDefault,
    ]);

    // Use controlled or uncontrolled selected property
    const selectedProperty =
      displayControlledSelected || internalSelected || effectiveDefault;

    const handlePropertySelect = (property: PropertyOption) => {
      if (loading) return;

      if (!displayControlledSelected) {
        setInternalSelected(property);
      }

      trackRumAction("map_property_selected", {
        property_id: property.id,
        property_label: property.label,
      });

      if (onPropertyChange) {
        onPropertyChange(property.id);
      }
    };

    const [expandedSections, setExpandedSections] = useState<Set<string>>(
      () => new Set(VARIABLE_TYPE_ORDER),
    );

    const toggleSection = useCallback((key: string) => {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    }, []);

    // Group properties by variable_type (model, weather, threat)
    const hasVariableTypes = displayProperties.some(
      (p) => p.type && ["model", "weather", "threat"].includes(p.type),
    );

    const groupedByType = useMemo(() => {
      if (!hasVariableTypes) return null;
      const acc: Record<string, PropertyOption[]> = {};
      displayProperties.forEach((property) => {
        const type = property.type || "model";
        if (!acc[type]) acc[type] = [];
        acc[type].push(property);
      });
      return acc;
    }, [displayProperties, hasVariableTypes]);

    const orderedSectionKeys = hasVariableTypes
      ? VARIABLE_TYPE_ORDER.filter((k) => groupedByType?.[k]?.length)
      : [];

    // Keep this after hooks to preserve consistent hook order across renders.
    if (!displayProperties || displayProperties.length === 0) {
      return (
        <div
          ref={ref}
          className={containerClassName}
          aria-busy={loading}
        >
          <div className="hidden px-6 pb-1 lg:block">
            <div className="text-sm font-medium">Selected Property</div>
          </div>
          <div className="lg:px-6">
          <Button
            variant="outline"
            size="sm"
            className={triggerClassName}
            disabled
          >
            <span className="min-w-0 truncate">
              {loading ? "Loading properties..." : "No properties"}
            </span>
            {loading ? (
              <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500 animate-pulse" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
            )}
          </Button>
          </div>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={containerClassName}
        aria-busy={loading}
      >
        <div className="hidden px-6 pb-1 lg:block">
          <div className="text-sm font-medium">Selected Property</div>
        </div>
        <div className="lg:px-6">
        <TooltipProvider delayDuration={300}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={triggerClassName}
                disabled={loading}
              >
                <span className="min-w-0 truncate">
                  {selectedProperty?.label || "Select property"}
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
              <DropdownMenuContent
                className={
                  hasVariableTypes
                    ? "z-[10001] max-h-[min(26rem,calc(100dvh-8rem))] w-[min(30rem,calc(100vw-1rem))] overflow-y-auto p-0"
                    : "z-[10001] max-h-[min(22rem,calc(100dvh-8rem))] w-[min(13rem,calc(100vw-1rem))] overflow-y-auto sm:w-48"
                }
              >
                {hasVariableTypes && groupedByType ? (
                  /* Vertically split sections: Model | Weather | Threat — collapsible on mobile */
                  <div className="grid grid-cols-1 gap-0 sm:grid-flow-col sm:auto-cols-[minmax(9rem,1fr)]">
                    {orderedSectionKeys.map((sectionKey) => {
                      const props = groupedByType[sectionKey] || [];
                      const isExpanded = expandedSections.has(sectionKey);
                      return (
                        <div
                          key={sectionKey}
                          className="flex min-w-0 flex-col border-b border-gray-200 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0"
                        >
                          <button
                            type="button"
                            onClick={() => toggleSection(sectionKey)}
                            className="flex w-full items-center justify-between rounded px-2 py-1.5 text-xs bg-gray-100 flex-shrink-0 mb-1 m-1.5 sm:cursor-default"
                          >
                            <div className="flex items-center space-x-2 min-w-0">
                              <span className="font-semibold uppercase tracking-wide text-gray-600 truncate">
                                {VARIABLE_TYPE_LABELS[sectionKey] || sectionKey}
                              </span>
                              <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 shrink-0">
                                ({props.length})
                              </span>
                            </div>
                            <ChevronDown
                              className={`h-3 w-3 shrink-0 text-gray-500 transition-transform sm:hidden ${isExpanded ? "rotate-180" : ""}`}
                            />
                          </button>
                          <div className={`max-h-48 overflow-y-auto py-1 sm:block sm:max-h-56 ${isExpanded ? "block" : "hidden"}`}>
                            {props.map((property) => (
                              <DropdownMenuItem
                                key={property.id}
                                onClick={() => handlePropertySelect(property)}
                                className="group flex items-center justify-between py-1.5 px-3 mx-1 rounded-sm"
                              >
                                <div className="flex flex-col min-w-0 flex-1">
                                  <div className="whitespace-normal text-sm font-medium leading-snug">
                                    {property.label}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0 ml-2">
                                  {property.description && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span
                                          className="opacity-0 group-hover:opacity-100 text-muted-foreground/50 hover:text-muted-foreground transition-opacity cursor-help"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Info className="h-3 w-3" />
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent
                                        side="right"
                                        className="max-w-[220px] text-xs leading-relaxed z-[10002]"
                                      >
                                        {property.description}
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                  {selectedProperty?.id === property.id && (
                                    <Check className="h-3.5 w-3.5 text-primary" />
                                  )}
                                </div>
                              </DropdownMenuItem>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Flat list when no variable types */
                  displayProperties.map((property) => (
                    <DropdownMenuItem
                      key={property.id}
                      onClick={() => handlePropertySelect(property)}
                      className="group flex items-center justify-between py-1"
                    >
                      <div className="flex flex-col min-w-0 flex-1">
                        <div className="break-words text-sm font-medium">
                          {property.label}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        {property.description && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="opacity-0 group-hover:opacity-100 text-muted-foreground/50 hover:text-muted-foreground transition-opacity cursor-help"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Info className="h-3 w-3" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side="right"
                              className="max-w-[220px] text-xs leading-relaxed z-[10002]"
                            >
                              {property.description}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {selectedProperty?.id === property.id && (
                          <Check className="h-3.5 w-3.5 shrink-0" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
          </DropdownMenu>
        </TooltipProvider>
        </div>
      </div>
    );
  },
);

MapPropertySelector.displayName = "MapPropertySelector";

export { MapPropertySelector };
